import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MdSave, MdCalendarToday, MdAccessTime, MdEventBusy } from "react-icons/md";
import attendanceService from "services/attendanceService";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import notify from "lib/notify";
import { getApiErrorMessage } from "api";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import { InputField, Textarea } from "components/fields";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { formatDate } from "utils/format";

const SERVICES_PATH = "/admin/attendance/services";

const COUNT_FIELDS = [
  { key: "attendance", label: "Total Attendance", hint: "Total number of people present", required: true },
  { key: "kids", label: "Kids", hint: "Children's ministry attendance" },
  { key: "salvations", label: "Salvations", hint: "Number of salvation decisions" },
  { key: "first_time_guests", label: "First-Time Visitors", hint: "Number of first-time visitors" },
  { key: "volunteers", label: "Volunteers", hint: "Number of volunteers serving" },
];

const EMPTY_METRICS = {
  attendance: 0,
  kids: 0,
  salvations: 0,
  volunteers: 0,
  contributions: 0,
  first_time_guests: 0,
  notes: "",
};

const selectOnFocus = (e) => e.target.select();

const AttendanceEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [service, setService] = useState(null);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);

  const loadServiceData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const serviceData = await attendanceService.getServiceInstanceById(id);
      setService(serviceData);

      if (serviceData?.metrics) {
        setMetrics({
          attendance: serviceData.metrics.attendance || 0,
          kids: serviceData.metrics.kids || 0,
          salvations: serviceData.metrics.salvations || 0,
          volunteers: serviceData.metrics.volunteers || 0,
          contributions: serviceData.metrics.contributions || 0,
          first_time_guests: serviceData.metrics.first_time_guests || 0,
          notes: serviceData.metrics.notes || "",
        });
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadServiceData();
  }, [loadServiceData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await attendanceService.upsertMetrics(id, metrics);
      track(EVENTS.ATTENDANCE_RECORDED, { service_id: id, count: metrics.attendance });
      notify.success("Attendance saved");
      navigate(SERVICES_PATH);
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save attendance"));
      setSaving(false);
    }
  };

  const handleMetricChange = (field, value) => {
    setMetrics((prev) => ({ ...prev, [field]: value }));
  };

  const backProps = { backTo: SERVICES_PATH, backLabel: "Back to Services" };

  if (loading) {
    return (
      <PageContainer variant="narrow">
        <PageHeader title="Service" {...backProps} />
        <Skeleton className="h-[480px] rounded-card" />
      </PageContainer>
    );
  }

  if (loadError || !service) {
    return (
      <PageContainer variant="narrow">
        <PageHeader title="Service" {...backProps} />
        <Card padding="md">
          {loadError ? (
            <ErrorState
              title="Couldn't Load Service"
              message={getApiErrorMessage(loadError, "Could not load service data")}
              onRetry={loadServiceData}
            />
          ) : (
            <EmptyState
              icon={MdEventBusy}
              title="Service Not Found"
              description="This service may have been removed."
            />
          )}
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer variant="narrow">
      <PageHeader
        title={service.serviceName || "Service"}
        {...backProps}
        description={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="flex items-center gap-2">
              <MdCalendarToday aria-hidden="true" />
              {service.serviceDate ? formatDate(service.serviceDate) : "Date not set"}
            </span>
            <span className="flex items-center gap-2">
              <MdAccessTime aria-hidden="true" />
              {service.serviceTime ? attendanceService.formatTime(service.serviceTime) : "Time not set"}
            </span>
          </span>
        }
      />

      <Card padding="md" className="gap-6">
        <CardHeader title="Enter Attendance Metrics" />
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {COUNT_FIELDS.map(({ key, label, hint, required }) => (
              <InputField
                key={key}
                type="number"
                min="0"
                label={label}
                hint={hint}
                required={required}
                value={metrics[key]}
                onChange={(e) => handleMetricChange(key, parseInt(e.target.value) || 0)}
                onFocus={selectOnFocus}
              />
            ))}
            <InputField
              type="number"
              min="0"
              step="0.01"
              label="Contributions ($)"
              hint="Total financial contributions"
              value={metrics.contributions}
              onChange={(e) => handleMetricChange("contributions", parseFloat(e.target.value) || 0)}
              onFocus={selectOnFocus}
              className="md:col-span-2"
            />
          </div>

          <Textarea
            label="Notes"
            value={metrics.notes}
            onChange={(e) => handleMetricChange("notes", e.target.value)}
            placeholder="Add any notes or observations about this service…"
          />

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => navigate(SERVICES_PATH)}>
              Cancel
            </Button>
            <Button type="submit" loading={saving} leftIcon={<MdSave aria-hidden="true" />}>
              {saving ? "Saving…" : "Save Attendance"}
            </Button>
          </div>
        </form>
      </Card>
    </PageContainer>
  );
};

export default AttendanceEntry;
